import angular from "angular";
import { ApiValidation, Culture, DataMapper, FileParser, Importable, ImportBatch, InputEnumOption } from "@salaxy/core";
import { EditDialogKnownActions, UiHelpers, WizardService, WizardStep } from "../../services";

/**
 * Controller that provides import functionality for any data.
 */
export class ImportController implements angular.IController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["UiHelpers", "WizardService", "$timeout"];

  /** The importable api. */
  private _api: Importable<any>;

  /** Locale for reading the raw data (CSV delimiter, numbers and dates). */
  public localeId: Culture = Culture.Fi;

  /** Available file parsers for import. */
  public fileParsers: FileParser[];

  /** Currently selected file parser.  */
  public currentFileParser: FileParser;

  /** Current mapper that is used to import the data */
  public mapper: DataMapper<any, any> = null;

  /** Wizard steps */
  public importSteps: WizardStep[] = [
    {
      title: "Liitä / lataa taulukko",
      view: "salaxy-components/modals/import/ImportStep1.html",
    },
    {
      title: "Asetukset",
      view: "salaxy-components/modals/import/ImportStep2.html",
    },
    {
      title: "Validointi",
      view: "salaxy-components/modals/import/ImportStep3.html",
    },
    {
      title: "Tallennus",
      view: "salaxy-components/modals/import/ImportStep4.html",
    },
  ];

  /** Raw data to import. */
  public batch: ImportBatch<any> = null;

  /** Progress indicator from 0 to 100 where 0 is not in progress. */
  public progress = 0;

  /**
   * Status messages from asynchronous process (import / validation to server).
   * Only shown if isInProgress = true;
   */
  public progressMessage: string = null;

  /** Results (errors and warnings) from the last run (import validation etc.). */
  public lastResult: ApiValidation = null;

  /** All object mappers. */
  private _objectMappers: InputEnumOption[] = null;

  /**
   * Constructor for Dependency Injection.
   * @param uiHelpers Salaxy ui helpers.
   * @param wizardService Wizard service.
   */
  constructor(private uiHelpers: UiHelpers, private wizardService: WizardService, private $timeout: angular.ITimeoutService) {

  }

  /** Gets or sets the Importable api that defines how the data is imported. */
  public get api():Importable<any> {
    return this._api;
  }
  public set api(value: Importable<any>) {
    this._api = value;
    this.createBatch();
  }

  /** Available object mappers for export */
  public get objectMappers(): InputEnumOption[] {
    if (!this._objectMappers) {
        this._objectMappers = this.api.getImportMappers();
        if (this._objectMappers?.length == 1) {
          this.setMapper(this._objectMappers[0].value);
        }
    }
    return this._objectMappers;
  }

  /** Clears the last validation result. */
  public clearLastResult() {
    if (this.batch) {
      this.batch.clearValidation();
    }
    this.lastResult = null;
  }

  /**
   * Shows import dialog.
   * @param api Optional API implementing Importable that is set to the controller.
   * The API must be set to the controller as property or in this parameter or showImport() will fail
   */
  public showImport(api: Importable<any> = null): void {
    if (api) {
      this.api = api;
    }
    if (!this.api) {
      throw new Error("Importable API not specified in showImport()");
    }
    this.wizardService.setSteps(this.importSteps);
    this.wizardService.activeStepNumber = 1;

    const dialogData = {
      $parent: this,
    };
    this.uiHelpers.showDialog(
      "salaxy-components/modals/import/Import.html",
      "WizardController",
      dialogData,
      null,
      "lg").then( () => {
        // Resolve promise here if needed in future versions.
      });
  }

  /** Paste data from clipboard. */
  public pasteFromClipboard(): Promise<string> {
    this.createBatch();
    return (navigator as any).clipboard.readText().then((text) => {
      this.batch.rawData = text;
      // Ensure refresh in UI
      return this.$timeout(() => {
        return text;
      });
    });
  }

  /**
   * Reads the file
   * @param file Selected file
   */
  public readFile(file: File): Promise<string> {
    if (!file) {
      return Promise.resolve(null);
    }
    // TODO detect if excel file and read content using Excel reader.
    if (file.name) {
      if (file.name.toLowerCase().match(/\.xl(s|sx)$/g)) {
        this.uiHelpers.showAlert("Tiedoston muotoa ei tueta", "Excel-tiedostot eivät ole vielä tuettuja. Käytä csv tai tab -muotoa.");
        return Promise.resolve(null);
      }
    }

    const read = new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result as string);
      };
      reader.onerror = () => reject;
      reader.readAsText(file);
    });

    this.createBatch();

    return read.then((text) => {
      this.batch.rawData = text;
      // Ensure refresh in UI
      return this.$timeout(() => {
        return text;
      });
    });
  }

  /**
   * Creates / resets the import batch as new using the current api
   * (should be selected before) and mapper (may be null / not yet selected).
   */
  public createBatch(): void {
    this.batch = new ImportBatch(this.mapper, this.api);
    this.fileParsers = this.api.parsers ?? [];
    this.currentFileParser = null;
  }

  /**
   * Read batch raw data.
   * @returns The status of the read as API validation object (also saved to lastResult).
   */
  public readBatch(): ApiValidation {
    if (!this.currentFileParser) {
      const parserGuess = FileParser.guessParser(this.fileParsers, this.batch.rawData);
      if (parserGuess) {
        this.currentFileParser = parserGuess.parser;
        this.localeId = parserGuess.locale;
      } else {
        return;
      }
    }
    this.lastResult = this.batch.read(null, this.currentFileParser, this.localeId);
    return this.lastResult;
  }

  /**
   * Sets the import mapper by ID.
   * @param id Identifier of the new object mapper.
   * @returns The mapper once it has been loaded / resolved.
   */
  public setMapper(id: string): Promise<DataMapper<any, any>> {
    return this.api.getImportMapper(id).then((mapper) => {
      this.mapper = mapper;
      this.batch.setMapper(mapper);
      this.readBatch();
      this.$timeout();
      return mapper;
    });
  }

  /** Read batch data */
  public mapBatch() {
    this.batch.map();
  }

  /** Validate batch data */
  public validateBatch() {
    this.setProgress(1, "Tarkistetaan...");
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const notify = (message, index, count, result) => {
      this.setProgress(index + 1, "Tarkistetaan...", count);
    };
    this.batch.validate(notify).then(() => {
      this.setProgress(0);
    });
  }

  /**
   * Imports the batch data.
   * @param $close Close function of the UI Bootstrap dialog.
   */
  public importBatch($close: (action: EditDialogKnownActions) => void): void {
    this.setProgress(1, "Tallennetaan... ");
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const notify = (message, index, count, result) => {
      this.setProgress(index + 1, "Tallennetaan...", count);
    };
    this.lastResult = null;
    this.batch.import(notify).then((result) => {
      this.setProgress(0);
      this.lastResult = result;
      if (!result.errors?.length) {
        $close(EditDialogKnownActions.Ok);
      }
    });
  }

  /**
   * Sets the progress indicator
   * @param value Value of the progress 0-100. If the process is just starting, use 1.
   * If not in progress, use 0. If the count is null, you may also use absolute value (index+1) here:
   * The system will convert it to 1-100 first assuming there is 10 items, then that there is 100 items.
   * @param message Progress message
   * @param count Total count of items if known.
   */
  private setProgress(value: number, message: string = null, count: number = null) {
    if (!value) {
      this.progress = 0;
      this.progressMessage = null;
      return;
    }
    if (count) {
      this.progress = value / count * 100;
    } else {
      if (value < 10) {
        this.progress = value * 10; // First assume 10 items
      } else if (value < 100) {
        this.progress = value;
      } else if (value < 1000) {
        this.progress = value / 10;
      } else {
        this.progress = 90;
      }
    }
    if (this.progress > 100) {
      this.progress = 100;
    }
    if (this.progress < 1) {
      this.progress = 1;
    }
    this.progressMessage = message ? message + " " : "";
    if (value != 1 && value != 100) {
      this.progressMessage += `(${value}${count ? "/" + count : ""})`;
    }
  }

  /** Toggles the debug view on/off. */
  public toggleDebug() {
    const isDebug = !(this as any).isDebug;
    (this as any).isDebug = isDebug;
    const dialog = document.getElementById("salaxyDialog");
    if (dialog) {
      if (isDebug) {
        dialog.style.width = "98vw";
      } else {
        dialog.style.width = null;
      }
    }
  }
}
